import { formatCalendarDate } from '@repo/design-system';
import type { LaunchHoursState } from './useLaunchHours';

/**
 * Every sentence the wizard says about whether opening hours let a twin launch: the Launch step's
 * line under *Go live*, its *Opening hours* summary block and its announcements (spec
 * twin-details-page-launch-gate, R7), and the Opening hours step's launch line (spec
 * twin-details-page-hours-week, R10) and its first-time note (twin-details-page-hours-start). One module, so none of them can disagree. Where a sentence
 * says where to fix the hours, it names the Opening hours step. Dates go through the design system's
 * `formatCalendarDate` (`3 Nov 2026`, from the date string itself, never re-zoned).
 */

/** What sits under *Go live* for the hours: nothing, the checking line, the failed banner, or a note. */
export type HoursLine = { kind: 'checking' } | { kind: 'failed' } | { kind: 'note'; text: string };

/** The line under *Go live*; null when the hours let the twin launch. `label` is *Go live* / *Update twin*. */
export const noteFor = (state: LaunchHoursState, label: string): HoursLine | null => {
  if (state.read === 'pending') return { kind: 'checking' };
  if (state.read === 'failed') return { kind: 'failed' };
  switch (state.launchHours.reason) {
    case 'covered':
    case 'upcoming':
      return null;
    case 'no_open_day':
      return {
        kind: 'note',
        text: `${label} needs opening hours. The standard week is closed every day — open at least one day on the Opening hours step.`,
      };
    case 'no_timezone':
      return {
        kind: 'note',
        text: `${label} needs opening hours, and they need the twin’s timezone. Set the timezone on Twin details first.`,
      };
    case 'no_standard':
      return {
        kind: 'note',
        text: `${label} needs opening hours. Add a standard week on the Opening hours step.`,
      };
  }
};

/**
 * The Opening hours step's launch line (spec twin-details-page-hours-week, R10.3; frame 06's footer):
 * whether these hours let `launchLabel` (*Launch Twin* / *Update Twin*) go ahead, and if not, why.
 * Null while the hours are being read or could not be read — the step shows its own read state then —
 * and for `no_timezone`, where the step points at Twin details instead of showing the week.
 */
export const stepFooterFor = (
  state: LaunchHoursState,
  launchLabel: string,
): { text: string; held: boolean } | null => {
  if (state.read !== 'ready') return null;
  const { reason, from } = state.launchHours;
  switch (reason) {
    case 'covered':
      return { text: `Covers today, so ${launchLabel} is unblocked`, held: false };
    case 'upcoming':
      return {
        text: `Starts on ${from ? formatCalendarDate(from) : 'a later date'}, so ${launchLabel} is unblocked`,
        held: false,
      };
    case 'no_open_day':
      return {
        text: `Closed every day, so ${launchLabel} is held. Open at least one day.`,
        held: true,
      };
    case 'no_standard':
      return {
        text: `No standard hours cover today or later, so ${launchLabel} is held.`,
        held: true,
      };
    case 'no_timezone':
      return null;
  }
};

/**
 * The note under the Opening hours step's quick starts (spec twin-details-page-hours-start, R2.6;
 * frame 05's rocket note): only while no standard covers today or later — the start panel's own
 * reason — and null otherwise, so it never contradicts the tick or Launch.
 */
export const startNoteFor = (state: LaunchHoursState, launchLabel: string): string | null =>
  state.read === 'ready' && state.launchHours.reason === 'no_standard'
    ? `${launchLabel} needs opening hours. Without them there are no daily figures to report.`
    : null;

/** One run of the summary block's sentence; `strong` runs are bold. */
export interface SummaryPart {
  text: string;
  strong?: boolean;
}

/** The *Opening hours* summary block's sentence. */
export const summaryFor = (state: LaunchHoursState, label: string): SummaryPart[] => {
  if (state.read === 'pending') return [{ text: 'Checking the twin’s opening hours…' }];
  if (state.read === 'failed') {
    return [
      { text: 'Could not read the twin’s opening hours.', strong: true },
      { text: ` ${label} waits until they can be checked.` },
    ];
  }
  const { reason, from } = state.launchHours;
  switch (reason) {
    case 'covered':
      return [
        { text: 'Standard hours from ' },
        { text: from ? formatCalendarDate(from) : 'their start', strong: true },
        { text: ' cover today.' },
      ];
    case 'upcoming':
      return [
        { text: 'Standard hours start on ' },
        { text: from ? formatCalendarDate(from) : 'a later date', strong: true },
        // Specials are published, so a special open before the standard starts is open: "closed"
        // alone would be false on those days (PR #2104 review).
        { text: '. Until then the twin reads as closed, apart from any special days.' },
      ];
    case 'no_open_day':
      return [
        { text: 'The standard week is closed every day.', strong: true },
        { text: ` ${label} needs at least one open day.` },
      ];
    case 'no_timezone':
      return [
        { text: 'This twin has no timezone', strong: true },
        { text: ', so its hours cannot be placed. Set it on Twin details.' },
      ];
    case 'no_standard':
      return [
        { text: 'No standard hours cover today or start later.', strong: true },
        { text: ` ${label} needs them.` },
      ];
  }
};

/**
 * A state reduced to what an announcement turns on. A *Check again* in flight is its own key, so
 * the region is cleared while it re-reads and a second failure is announced again, as the lock's
 * is (spec twin-details-page-launch-gate, R7.3).
 */
export const announcementKey = (state: LaunchHoursState, rechecking = false): string =>
  rechecking
    ? 'rechecking'
    : state.read === 'ready'
      ? state.launchHours.ready
        ? 'ready'
        : state.launchHours.reason
      : state.read;

/**
 * What the polite region says when the hours state changes, or null to leave it as it is.
 * Transitions only: `previous` is undefined the first time, so a step that opens already held
 * announces it once.
 */
export const announcementFor = (
  previous: string | undefined,
  state: LaunchHoursState,
  label: string,
  rechecking = false,
): string | null => {
  const next = announcementKey(state, rechecking);
  if (next === previous) return null;
  if (next === 'pending' || next === 'rechecking') return '';
  if (next === 'failed') return 'Couldn’t check this twin’s opening hours.';
  if (next === 'ready') {
    return previous === undefined || previous === 'pending'
      ? null
      : `${label} is no longer held by opening hours.`;
  }
  const line = noteFor(state, label);
  return line?.kind === 'note' ? line.text : null;
};
